import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Check, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { toast } from '../../../../../hooks/use-toast';
import { supabase } from '../../../../../integrations/supabase/client';

interface PromptEdit {
  action: 'replace' | 'insert_after' | 'insert_before' | 'delete';
  search_text: string;
  replace_text?: string;
  explanation: string;
}

interface InvestigationStep {
  tool: string;
  label: string;
  status: 'executing' | 'completed';
  summary?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  edits?: PromptEdit[];
  appliedEdits?: boolean[];
  steps?: InvestigationStep[];
}

interface PromptChatProps {
  systemPrompt: string;
  onApplyEdit: (newPrompt: string) => void;
  agentName: string;
  agentId: string;
}

const CHAT_URL = `https://voknbpgkjaabhqhhfihr.supabase.co/functions/v1/prompt-editor-chat-v2`;

export function PromptChat({ systemPrompt, onApplyEdit, agentName, agentId }: PromptChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load session on mount
  useEffect(() => {
    if (!agentId) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('prompt_editor_sessions')
        .select('id, messages')
        .eq('user_id', user.id)
        .eq('agent_id', agentId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setSessionId(data.id);
        const saved = data.messages as ChatMessage[];
        if (saved && saved.length > 0) {
          // Restore messages but mark all edits as already applied
          setMessages(saved.map(m => ({
            ...m,
            appliedEdits: m.edits ? m.edits.map(() => true) : undefined,
          })));
        }
      }
    })();
  }, [agentId]);

  // Save session after messages change (debounced)
  useEffect(() => {
    if (messages.length === 0 || isLoading) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Strip steps from saved messages to keep storage lean
      const toSave = messages.map(m => ({
        role: m.role,
        content: m.content,
        edits: m.edits,
      }));

      if (sessionId) {
        await supabase
          .from('prompt_editor_sessions')
          .update({ messages: toSave, updated_at: new Date().toISOString() })
          .eq('id', sessionId);
      } else {
        const { data } = await supabase
          .from('prompt_editor_sessions')
          .insert({ user_id: user.id, agent_id: agentId, messages: toSave })
          .select('id')
          .single();
        if (data) setSessionId(data.id);
      }
    }, 1500);

    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [messages, isLoading, agentId, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const clearSession = async () => {
    setMessages([]);
    if (sessionId) {
      await supabase.from('prompt_editor_sessions').delete().eq('id', sessionId);
      setSessionId(null);
    }
  };

  const applyEdit = useCallback((edit: PromptEdit, messageIndex: number, editIndex: number) => {
    let newPrompt = systemPrompt;

    if (!systemPrompt.includes(edit.search_text)) {
      toast.error('No se encontró el texto a editar. El prompt pudo haber cambiado.');
      return;
    }

    switch (edit.action) {
      case 'replace':
        if (edit.replace_text !== undefined) {
          newPrompt = systemPrompt.replace(edit.search_text, edit.replace_text);
        }
        break;
      case 'insert_after':
        if (edit.replace_text !== undefined) {
          newPrompt = systemPrompt.replace(edit.search_text, edit.search_text + '\n' + edit.replace_text);
        }
        break;
      case 'insert_before':
        if (edit.replace_text !== undefined) {
          newPrompt = systemPrompt.replace(edit.search_text, edit.replace_text + '\n' + edit.search_text);
        }
        break;
      case 'delete':
        newPrompt = systemPrompt.replace(edit.search_text, '');
        break;
    }

    onApplyEdit(newPrompt);

    setMessages(prev => prev.map((msg, i) => {
      if (i === messageIndex && msg.appliedEdits) {
        const newApplied = [...msg.appliedEdits];
        newApplied[editIndex] = true;
        return { ...msg, appliedEdits: newApplied };
      }
      return msg;
    }));

    toast.success(edit.explanation || 'Cambio aplicado');
  }, [systemPrompt, onApplyEdit]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const apiMessages = newMessages.map(m => ({ role: m.role, content: m.content }));

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZva25icGdramFhYmhxaGhmaWhyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTg2NTQ2MzEsImV4cCI6MjA3NDIzMDYzMX0.u-8mYkp3Ns_8PvOL9TC5-L2xbUihKFojQ7rFByJo7Qw`,
        },
        body: JSON.stringify({ messages: apiMessages, systemPrompt, agentId, agentName }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(err.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let assistantContent = '';
      let toolCalls: any[] = [];
      let currentToolCall: any = null;
      let investigationSteps: InvestigationStep[] = [];

      const updateAssistant = (content: string, edits?: PromptEdit[], steps?: InvestigationStep[]) => {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) =>
              i === prev.length - 1
                ? { ...m, content, edits, steps: steps || m.steps, appliedEdits: edits ? edits.map(() => false) : undefined }
                : m
            );
          }
          return [...prev, {
            role: 'assistant' as const,
            content,
            edits,
            steps,
            appliedEdits: edits ? edits.map(() => false) : undefined,
          }];
        });
      };

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') { streamDone = true; break; }

          try {
            const parsed = JSON.parse(jsonStr);

            // Custom agentic step events
            if (parsed.type === 'step') {
              const existingIdx = investigationSteps.findIndex(s => s.tool === parsed.tool && s.status === 'executing');
              if (parsed.status === 'completed' && existingIdx >= 0) {
                investigationSteps[existingIdx] = { ...investigationSteps[existingIdx], status: 'completed', summary: parsed.summary };
              } else if (parsed.status === 'executing') {
                investigationSteps.push({ tool: parsed.tool, label: parsed.label || parsed.tool, status: 'executing' });
              }
              investigationSteps = [...investigationSteps];
              updateAssistant(assistantContent || '', undefined, investigationSteps);
              continue;
            }

            // Error events
            if (parsed.type === 'error') {
              assistantContent += parsed.message || 'Error desconocido';
              updateAssistant(assistantContent, undefined, investigationSteps);
              continue;
            }

            const delta = parsed.choices?.[0]?.delta;
            if (!delta) continue;

            // Text content
            if (delta.content) {
              assistantContent += delta.content;
              updateAssistant(assistantContent, undefined, investigationSteps);
            }

            // Tool calls
            if (delta.tool_calls) {
              for (const tc of delta.tool_calls) {
                if (tc.index !== undefined) {
                  while (toolCalls.length <= tc.index) {
                    toolCalls.push({ id: '', function: { name: '', arguments: '' } });
                  }
                  currentToolCall = toolCalls[tc.index];
                }
                if (tc.id) currentToolCall.id = tc.id;
                if (tc.function?.name) currentToolCall.function.name = tc.function.name;
                if (tc.function?.arguments) currentToolCall.function.arguments += tc.function.arguments;
              }
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Process tool calls into edits
      const edits: PromptEdit[] = [];
      for (const tc of toolCalls) {
        if (tc.function.name === 'apply_prompt_edit') {
          try {
            const args = JSON.parse(tc.function.arguments);
            edits.push({
              action: args.action,
              search_text: args.search_text,
              replace_text: args.replace_text,
              explanation: args.explanation,
            });
          } catch (e) {
            console.error('Failed to parse tool call args:', e);
          }
        }
      }

      updateAssistant(assistantContent || (edits.length > 0 ? 'He preparado los siguientes cambios:' : ''), edits.length > 0 ? edits : undefined, investigationSteps.length > 0 ? investigationSteps : undefined);
    } catch (e) {
      console.error('Chat error:', e);
      toast.error(e instanceof Error ? e.message : 'Error al enviar mensaje');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'replace': return 'Reemplazar';
      case 'insert_after': return 'Insertar después';
      case 'insert_before': return 'Insertar antes';
      case 'delete': return 'Eliminar';
      default: return action;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] max-h-[600px]">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="text-foreground font-semibold text-base">Editor IA</h2>
        <span className="text-xs text-muted-foreground ml-auto">minimax-m2.7</span>
        {messages.length > 0 && (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={clearSession}
            title="Limpiar historial"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1 min-h-0">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8 opacity-60">
            <Sparkles className="w-8 h-8 text-primary mb-3" />
            <p className="text-sm text-muted-foreground">
              Describe qué quieres cambiar en tu prompt y la IA lo editará por ti.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Ej: "Agrega que no hable de la competencia"
            </p>
          </div>
        )}

        {messages.map((msg, msgIdx) => (
          <div key={msgIdx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/50 text-foreground border border-border'
            }`}>
              {/* Investigation steps */}
              {msg.steps && msg.steps.length > 0 && (
                <div className="mb-2 space-y-1">
                  {msg.steps.map((step, stepIdx) => (
                    <div key={stepIdx} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      {step.status === 'executing' ? (
                        <Loader2 className="w-3 h-3 animate-spin text-primary" />
                      ) : (
                        <Check className="w-3 h-3 text-emerald-500" />
                      )}
                      <span>{step.label}</span>
                      {step.summary && (
                        <span className="opacity-60">— {step.summary}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {msg.content && (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              )}

              {/* Edit suggestions */}
              {msg.edits && msg.edits.length > 0 && (
                <div className="mt-3 space-y-3">
                  {msg.edits.map((edit, editIdx) => {
                    const isApplied = msg.appliedEdits?.[editIdx];
                    return (
                      <div key={editIdx} className={`rounded-lg border overflow-hidden transition-all ${isApplied ? 'border-emerald-500/30 opacity-75' : 'border-border'}`}>
                        {/* Edit header */}
                        <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide uppercase ${
                              edit.action === 'delete' ? 'bg-red-500/10 text-red-600' : 'bg-primary/10 text-primary'
                            }`}>
                              {getActionLabel(edit.action)}
                            </span>
                          </div>
                          {isApplied ? (
                            <span className="text-xs flex items-center gap-1 text-emerald-600 font-medium">
                              <Check className="w-3 h-3" /> Aplicado
                            </span>
                          ) : (
                            <button
                              className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                              onClick={() => applyEdit(edit, msgIdx, editIdx)}
                            >
                              Aplicar cambio
                            </button>
                          )}
                        </div>

                        {/* Explanation */}
                        <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border/50">
                          {edit.explanation}
                        </div>

                        {/* Diff view */}
                        <div className="text-xs font-mono">
                          {edit.action === 'delete' && (
                            <div className="px-3 py-2 bg-red-500/5 text-red-700/70 line-through whitespace-pre-wrap">
                              {edit.search_text}
                            </div>
                          )}
                          {edit.action === 'replace' && (
                            <>
                              <div className="px-3 py-2 bg-red-500/5 text-red-700/60 line-through whitespace-pre-wrap border-b border-border/30">
                                {edit.search_text}
                              </div>
                              <div className="px-3 py-2 bg-emerald-500/5 text-emerald-800 whitespace-pre-wrap">
                                {edit.replace_text}
                              </div>
                            </>
                          )}
                          {(edit.action === 'insert_after' || edit.action === 'insert_before') && edit.replace_text && (
                            <div className="px-3 py-2 bg-emerald-500/5 text-emerald-800 whitespace-pre-wrap">
                              {edit.replace_text}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && messages[messages.length - 1]?.role === 'user' && (
          <div className="flex justify-start">
            <div className="bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="text-muted-foreground">Investigando...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-end gap-2 border-t border-border pt-3">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe el cambio que quieres..."
          className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[38px] max-h-[100px]"
          rows={1}
          disabled={isLoading}
        />
        <Button
          size="icon"
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          className="h-[38px] w-[38px] flex-shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
